import { useEffect, useRef, useState } from 'react';
import { FileUploader } from 'react-drag-drop-files';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import { ImageListType, ImageType } from 'react-images-uploading';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { X } from 'lucide-react';

import Button from '../../components/Buttons/Button';
import cn from '../../lib/classNames';

import 'react-image-crop/src/ReactCrop.scss';
import styles from './UploadFile.module.scss';

const TO_RADIANS = Math.PI / 180;

interface ImageUploadingButtonProps {
	// eslint-disable-next-line no-unused-vars
	onChange: (imageList: ImageListType) => void;
	acceptType?: string[];
	maxFileSize?: number; // size of the file (number in mb)
}

function ImageUploadingButton({
	onChange,
	acceptType = ['jpg', 'png', 'jpeg'],
	maxFileSize = 5,
}: ImageUploadingButtonProps) {
	const [isError, setIsError] = useState(false);

	return (
		<FileUploader
			handleChange={(value: ImageListType) => {
				onChange(value);
				setIsError(false);
			}}
			name="file"
			types={acceptType}
			maxSize={maxFileSize}
			onTypeError={() => setIsError(true)}
			onSizeError={() => setIsError(true)}
		>
			<div className={cn(styles['upload-image-area'], { [styles.error]: isError })}>
				Click here or drag and drop your logo image into this area
			</div>
		</FileUploader>
	);
}

interface ImagePreviewProps {
	file: File | null;
	imgRef: React.RefObject<HTMLImageElement>;
}

function ImagePreview({ file, imgRef }: ImagePreviewProps) {
	const [imageUrl, setImageUrl] = useState('');

	useEffect(() => {
		if (file) {
			const url = URL.createObjectURL(file);
			setImageUrl(url);

			return () => URL.revokeObjectURL(url);
		}
	}, [file]);

	if (!file) return null;

	return (
		<img
			ref={imgRef}
			src={imageUrl}
			alt="Preview"
			style={{ maxWidth: '530px', maxHeight: '300px' }}
		/>
	);
}

interface ImageCropperProps {
	open: boolean;
	image: ImageType | undefined;
	// eslint-disable-next-line no-unused-vars
	onComplete: (file: File | undefined) => void;
	containerStyle?: React.CSSProperties;
	[key: string]: any;
}

function ImageCropper({ open, image, onComplete, containerStyle }: ImageCropperProps) {
	const imgRef = useRef<HTMLImageElement>(null);

	const defaultCrop = {
		unit: '%', // Can be 'px' or '%'
		x: 0,
		y: 0,
		width: 100,
		height: 100,
	};

	const [crop, setCrop] = useState<Crop>(defaultCrop as Crop);
	const [completedCrop, setCompletedCrop] = useState<PixelCrop>();

	const onFinishCrop = (file: File | undefined) => {
		onComplete(file);
		setCrop(defaultCrop as Crop);
		setCompletedCrop(undefined);
	};

	async function cropImageNow() {
		if (!imgRef.current || !completedCrop) {
			console.error('Crop canvas does not exist');
			onFinishCrop(undefined);
			return;
		}
		const { naturalWidth, naturalHeight, width, height } = imgRef.current;

		const scale = 1;
		const rotate = 0;

		const canvas = document.createElement('canvas');

		const ctx = canvas.getContext('2d');

		if (!ctx) {
			console.error('No 2d context');
			onFinishCrop(undefined);
			return;
		}

		const scaleX = naturalWidth / width;
		const scaleY = naturalHeight / height;
		// devicePixelRatio slightly increases sharpness on retina devices
		// at the expense of slightly slower render times and needing to
		// size the image back down if you want to download/upload and be
		// true to the images natural size.
		const pixelRatio = window.devicePixelRatio;

		canvas.width = Math.floor(completedCrop.width * scaleX * pixelRatio);
		canvas.height = Math.floor(completedCrop.height * scaleY * pixelRatio);

		ctx.scale(pixelRatio, pixelRatio);
		ctx.imageSmoothingQuality = 'high';

		const cropX = completedCrop.x * scaleX;
		const cropY = completedCrop.y * scaleY;

		const rotateRads = rotate * TO_RADIANS;
		const centerX = naturalWidth / 2;
		const centerY = naturalHeight / 2;

		ctx.save();

		ctx.translate(-cropX, -cropY);
		ctx.translate(centerX, centerY);
		ctx.rotate(rotateRads);
		ctx.scale(scale, scale);
		ctx.translate(-centerX, -centerY);
		ctx.drawImage(
			imgRef.current,
			0,
			0,
			naturalWidth,
			naturalHeight,
			0,
			0,
			naturalWidth,
			naturalHeight,
		);

		ctx.restore();

		canvas.toBlob((blob) => {
			if (blob) {
				const file = new File([blob], 'fileName.png', { type: 'image/png' });
				onFinishCrop(file);
			} else {
				onFinishCrop(undefined);
			}
		}, 'image/png');
	}

	return (
		<Dialog open={open} maxWidth="sm" fullWidth className={styles.modal}>
			<DialogTitle>
				<span>Crop Photo</span>
				<a onClick={() => onFinishCrop(undefined)}><X size={32} /></a>
			</DialogTitle>

			<DialogContent>
				<div style={{ border: '1px solid #7D8C97', borderRadius: '2px', padding: '10px' }}>
					<div style={containerStyle}>
						<ReactCrop
							crop={crop}
							onChange={(_, percentCrop) => setCrop(percentCrop)}
							onComplete={(c) => setCompletedCrop(c)}
							maxWidth={300}
							maxHeight={300}
						>
							{image && <ImagePreview imgRef={imgRef} file={(image as File) || null} />}
						</ReactCrop>
					</div>
				</div>
			</DialogContent>

			<DialogActions>
				<Button onClick={() => onFinishCrop(undefined)} variant="outlined" color="primary">
					CANCEL
				</Button>
				<Button
					variant="filled"
					color="primary"
					// onClick={() => onComplete(cropImage(image, croppedAreaPixels, console.log))}
					onClick={cropImageNow}
				>
					SAVE
				</Button>
			</DialogActions>
		</Dialog>
	);
}

type UploadFileType = {
	acceptType?: string[];
	// eslint-disable-next-line no-unused-vars
	onSelect: (imageList: ImageListType) => void;
	maxFileSize?: number;
};

function UploadFile(props: UploadFileType) {
	const { acceptType, onSelect, maxFileSize } = props;
	const [image, setImage] = useState<ImageType | undefined>(undefined);
	const [dialogOpen, setDialogOpen] = useState(false);

	const onChange = (imageList: ImageListType) => {
		setDialogOpen(true);
		setImage(imageList);
	};

	return (
		<div className={styles['upload-image-wrap']}>
			<ImageUploadingButton acceptType={acceptType} maxFileSize={maxFileSize} onChange={onChange} />

			<span className={styles['upload-image-text']}>
				Upload your company logo (recommended format: PNG or JPEG, up to 5 MB)
			</span>

			<ImageCropper
				open={dialogOpen}
				image={image}
				onComplete={(imagePromisse: File | undefined) => {
					setDialogOpen(false);
					if (image){
						onSelect(imagePromisse ? [imagePromisse] : ([image] as File[]));
					}
				}}
				containerStyle={{
					position: 'relative',
					width: '100%',
					height: 300,
					background: 'white',
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
				}}
			/>
		</div>
	);
}

export default UploadFile;
